import { useEffect, useMemo, useState } from "react";
import { PayloadAction } from "@reduxjs/toolkit";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import {
  AirSavedCardModel,
  airSavedCardStore,
  GetAirCardsFunc,
  isAirSavedCardModel,
  useAirSavedCardTagsMutation,
} from "entities/AirSavedCard";
import { searchFilterStore } from "entities/SearchFilter";
import {
  GetSeaCardsFunc,
  isSeaSavedShipmentModel,
  SeaSavedCardModel,
  seaSavedCardStore,
  useSeaSavedCardTagsMutation,
} from "entities/SeaSavedCard";
import { useAppSelector } from "shared/lib/redux";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { Portal } from "shared/ui/Portal";
import { Button } from "./Button";
import { EditTagsForm } from "./EditTagsForm";
import styles from "./EditTags.module.scss";

type SavedCardModel = SeaSavedCardModel | AirSavedCardModel;
type SavedCardArray = SeaSavedCardModel[] | AirSavedCardModel[];

type EditTagsProps = {
  openedCard?: Nullable<ShipmentModel>;
  cardsByTag: SavedCardArray;
  allTags: string[];
  getCardsByTags: GetSeaCardsFunc | GetAirCardsFunc;
  activeTag: string | null;
  setActiveTag: (tag: string | null) => void;
  refreshTags: () => void;
  setCardsByTagAction: (cards: SavedCardArray) => PayloadAction<SavedCardArray>;
};

const getSavedCardsByType = (
  shipment: ValueOf<typeof ShipmentType>,
  seaCards: SeaSavedCardModel[],
  airCards: AirSavedCardModel[]
): SavedCardArray => {
  switch (shipment) {
    case ShipmentType.SEA:
      return seaCards;
    case ShipmentType.AIR:
      return airCards;
    default:
      return [];
  }
};

const isSavedCardModel = (card: Nullable<{ responseType: string }>): card is SavedCardModel => {
  return isSeaSavedShipmentModel(card) || isAirSavedCardModel(card);
};

export const EditTags: RFC<EditTagsProps> = ({
  openedCard,
  cardsByTag,
  allTags,
  getCardsByTags,
  activeTag,
  setActiveTag,
  refreshTags,
  setCardsByTagAction,
}) => {
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const [isOpen, setOpen] = useState(false);
  const { editAirTagsFromCards, isAirTagsProcessing } = useAirSavedCardTagsMutation();
  const { editSeaTagsFromCards, isSeaTagsProcessing } = useSeaSavedCardTagsMutation();
  const [tags, setTags] = useState<string[]>([]);
  const [cardId, setCardId] = useState<number | null>(null);

  const seaSavedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const airSavedCards = useAppSelector(airSavedCardStore.selectors.savedCards);

  const savedCards = useMemo(
    () => getSavedCardsByType(shipment, seaSavedCards, airSavedCards),
    [shipment, seaSavedCards, airSavedCards]
  );

  const isTagsProcessing = useMemo(() => {
    switch (shipment) {
      case ShipmentType.AIR:
        return isAirTagsProcessing;
      case ShipmentType.SEA:
        return isSeaTagsProcessing;
      default:
        return false;
    }
  }, [shipment, isAirTagsProcessing, isSeaTagsProcessing]);

  const editTagsFromCards = useMemo(() => {
    switch (shipment) {
      case ShipmentType.AIR:
        return editAirTagsFromCards;
      case ShipmentType.SEA:
        return editSeaTagsFromCards;
      default:
        return () => {
          /* empty */
        };
    }
  }, [shipment, editAirTagsFromCards, editSeaTagsFromCards]);

  useEffect(() => {
    const isOpenedCardSaved =
      savedCards.find((card) => card.reference === openedCard?.reference) ??
      cardsByTag.find((card) => card.reference === openedCard?.reference);

    if (isSavedCardModel(isOpenedCardSaved)) {
      setTags(isOpenedCardSaved.tags);
      setCardId(isOpenedCardSaved.id);
    } else {
      setTags([]);
      setCardId(null);
    }
  }, [openedCard?.rawData, savedCards.length]);

  const closePopup = () => {
    setOpen(false);
  };

  if (!cardId) return null;

  return (
    <>
      <Button
        onClick={() => {
          setOpen(!isOpen);
        }}
        count={tags.length}
        data-test-id={`openedCard-action-tags-${openedCard?.reference}`}
      />
      {isOpen && (
        <Portal
          closePopup={() => {
            if (!isTagsProcessing) closePopup();
          }}
          className={styles.editTagsPopup}
        >
          <EditTagsForm
            setNewTags={setTags}
            tags={tags}
            allTags={allTags}
            closePopup={closePopup}
            cardId={cardId}
            editTagsFromCards={editTagsFromCards}
            isTagsProcessing={isTagsProcessing}
            getCardsByTags={getCardsByTags}
            activeTag={activeTag}
            setActiveTag={setActiveTag}
            refreshTags={refreshTags}
            cardsByTag={cardsByTag}
            setCardsByTagAction={setCardsByTagAction}
          />
        </Portal>
      )}
    </>
  );
};
